"use client";

import Link from "next/link";
import Image from "next/image";
import { privacyTerms } from '@/styles/styles';

export default function PrivacyTermsPage() {
    return (
        <div className={privacyTerms.shell}>
            {/* Top Bar */}
            <div className={privacyTerms.header}>
                <div>
                    <Link href="/">
                        <Image src="/logo.png" alt="Logo" width={300} height={300} unoptimized className={privacyTerms.logo} />
                    </Link>
                </div>
                <div>
                    <Link href="/login">
                        <button className={privacyTerms.loginButton}>
                            Login
                        </button>
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <main className={privacyTerms.main}>
                <div className={privacyTerms.prose}>
                    <h1 className={privacyTerms.pageTitle}>
                        Privacy Notice
                    </h1>

                    {/* Privacy Notice Section */}
                    <section className={privacyTerms.section}>
                        <h2 className={privacyTerms.sectionTitle}>Privacy Notice</h2>

                        <div className={privacyTerms.body}>
                            <p>
                                <strong>Effective Date:</strong> January 1, 2025
                            </p>

                            <h3 className={privacyTerms.heading}>1. Purpose of this Privacy Notice</h3>
                            <p>
                                Aralin Insights values your privacy. This Privacy Notice explains what personal data we collect, why we collect it, and how it is processed in line with the Data Privacy Act of 2012, our Data Sharing Agreement with your school, and the Aralin Data Privacy Manual.
                                <br /> <br />
                                By using our platform, you acknowledge that you have read and understood this Privacy Notice and consent to the processing of your personal data as described below.
                            </p>

                            <h3 className={privacyTerms.heading}>2. Personal Data We Collect</h3>
                            <p>
                                We only collect the minimum personal data necessary to deliver our services:
                            </p>
                            <ul className={privacyTerms.list}>
                                <li>Full Name</li>
                                <li>Grade Level</li>
                                <li>Section</li>
                            </ul>
                            <p>
                                No other personal data (such as contact details, demographics, or sensitive information) is required for your use of Aralin Insights.
                            </p>

                            <h3 className={privacyTerms.heading}>3. How We Use Your Data</h3>
                            <p>
                                Your personal data is used only for educational purposes, specifically:
                            </p>
                            <ul className={privacyTerms.list}>
                                <li>To generate your login credentials.</li>
                                <li>To properly tag your exam results and learning data under your name, section, and grade level.</li>
                                <li>To provide individual and school-wide analytics, enabling teachers and administrators to understand performance trends and learning needs.</li>
                                <li>To support aggregated data reports for your school’s use in monitoring and improving learning outcomes.</li>
                            </ul>
                            <p>We do not sell, share, or repurpose your personal data for marketing or unrelated purposes.</p>

                            <h3 className={privacyTerms.heading}>4. Data Sharing</h3>
                            <p>
                                Your data remains the property of your school. It is only shared under the scope of the Data Sharing Agreement with your school.
                            </p>
                            <ul className={privacyTerms.list}>
                                <li><strong>Within Aralin:</strong>
                                    <ul className={privacyTerms.nestedList}>
                                        <li>Access is role-based and limited to authorized personnel.</li>
                                    </ul>
                                </li>
                                <li><strong>With Third-Party Providers:</strong>
                                    <ul className={privacyTerms.nestedList}>
                                        <li>AWS Lightsail (Singapore) stores account credentials securely.</li>
                                        <li>OpenAI processes anonymized answer data for analytics only. No names, emails, or identifiers are sent.</li>
                                    </ul>
                                </li>
                            </ul>

                            <h4 className={privacyTerms.subheading}>Data Ownership</h4>
                            <div className={privacyTerms.indent}>
                                <p>
                                    All personal data collected through Aralin Insights, including names, grade levels, sections, and any related performance analytics, shall remain the property of the school that provided access to the platform.
                                </p>
                                <br />
                                <p>
                                    Aralin acts only as a processor of such data, performing tagging, analytics, and reporting strictly on behalf of the school and for the educational purposes defined in the Data Sharing Agreement.
                                </p>
                                <br />
                                <p><strong>
                                    By agreeing to this Privacy Notice, you acknowledge that:
                                </strong>
                                </p>
                                <br />
                                <ul className={privacyTerms.list}>
                                    <li>The school is the lawful owner and controller of your data.</li>
                                    <li>Aralin does not claim any ownership rights over your personal data.</li>
                                    <li>Aralin will not use or disclose your data beyond the purposes expressly authorized by the school and the Data Privacy Act of 2012.</li>
                                </ul>
                            </div>

                            <h3 className={privacyTerms.heading}>5. Data Retention and Disposal</h3>
                            <p>
                                Your data will be kept only for as long as necessary to fulfill the purposes above, typically two academic years from your last activity. After this period, it will be securely deleted unless your school requests retention for academic tracking.
                            </p>

                            <h3 className={privacyTerms.heading}>6. Your Rights as a Data Subject</h3>
                            <p>
                            Under the Data Privacy Act, you have the right to:
                            </p>
                            <ul className={privacyTerms.list}>
                                <li>Be informed of how your data is used.</li>
                                <li>Access, correct, or request deletion of your data.</li>
                                <li>Object to processing in certain cases.</li>
                                <li>File complaints with the National Privacy Commission.</li>
                            </ul>
                            <p>All requests may be directed to Aralin’s Data Protection Officer (DPO).</p>

                            <h3 className={privacyTerms.heading}>7. Contact Information</h3>
                            <p>
                            For any questions, concerns, or requests regarding your data, please contact: <br />

                            <strong>Data Protection Officer (DPO)
                            <br />
                            Ana Katrina P. De Jesus
                            <br />
                            Email: dataprotection@aralinonline.com
                            </strong>
                            </p>

                            <h3 className={privacyTerms.heading}>8. Consent</h3>
                            <p>
                            By using our platform, you confirm that:
                            </p>
                            <ul className={privacyTerms.list}>
                                <li>You have read and understood this Privacy Notice.</li>
                                <li>You consent to the collection and processing of your name, grade level, and section for the purposes stated above.</li>
                                <li>You understand that your school remains the owner of your data, and Aralin acts only as a data processor under the agreed purposes.</li>
                            </ul>
                        </div>
                    </section>

                </div>
            </main>

            {/* Footer */}
            <footer className={privacyTerms.footer}>
                <div className={privacyTerms.footerCopy}>
                    © 2025{" "}
                    <span className={privacyTerms.brand}>
                        Aralin Insights
                    </span>{" "}
                    — All rights reserved.
                </div>
                <div>
                    <Link href="/">
                        <span className={privacyTerms.homeLink}>
                            Back to Home
                        </span>
                    </Link>
                </div>
            </footer>
        </div>
    );
}
